import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiPhone,
  FiCalendar,
  FiBookOpen,
  FiAward,
  FiTrendingUp,
  FiRefreshCw,
  FiGrid,
  FiList,
  FiBarChart2,
  FiSearch
} from 'react-icons/fi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { studentsAPI } from '../services/api';

const badgeClass = (v) => ({ success: 'bg-emerald-100 text-emerald-800', primary: 'bg-slate-100 text-slate-800', warning: 'bg-amber-100 text-amber-800', danger: 'bg-red-100 text-red-800', secondary: 'bg-slate-100 text-slate-600' }[v] || 'bg-slate-100 text-slate-600');

const Students = () => {
  const { showError, showSuccess } = useNotification();
  const { user } = useAuth();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Filter options
  const filterOptions = [
    { value: 'all', label: 'All Students' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'high-performer', label: 'High Performers' },
    { value: 'needs-attention', label: 'Needs Attention' }
  ];

  // Sort options
  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'email', label: 'Email' },
    { value: 'createdAt', label: 'Join Date' },
    { value: 'performance', label: 'Performance' },
    { value: 'lastActive', label: 'Last Active' }
  ];

  // Fetch students data
  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await studentsAPI.getStudents();
      
      if (response.data && response.data.users) {
        setStudents(response.data.users);
        setFilteredStudents(response.data.users);
      } else {
        setStudents([]);
        setFilteredStudents([]);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      showError('Failed to load students');
      setStudents([]);
      setFilteredStudents([]);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  // Filter and search students
  const filterStudents = useCallback(() => {
    let filtered = [...students];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(student =>
        student.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (selectedFilter !== 'all') {
      switch (selectedFilter) {
        case 'active':
          filtered = filtered.filter(student => student.status === 'active');
          break;
        case 'inactive':
          filtered = filtered.filter(student => student.status !== 'active');
          break;
        case 'high-performer':
          filtered = filtered.filter(student => student.performance?.averageScore >= 80);
          break;
        case 'needs-attention':
          filtered = filtered.filter(student => student.performance?.averageScore < 60);
          break;
        default:
          break;
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case 'email':
          aValue = a.email?.toLowerCase() || '';
          bValue = b.email?.toLowerCase() || '';
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'performance':
          aValue = a.performance?.averageScore || 0;
          bValue = b.performance?.averageScore || 0;
          break;
        case 'lastActive':
          aValue = new Date(a.lastActive || a.updatedAt);
          bValue = new Date(b.lastActive || b.updatedAt);
          break;
        default:
          aValue = a[sortBy];
          bValue = b[sortBy];
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredStudents(filtered);
    setCurrentPage(1);
  }, [students, searchTerm, selectedFilter, sortBy, sortOrder]);

  // Get performance badge
  const getPerformanceBadge = (score) => {
    if (score >= 90) return { text: 'Excellent', variant: 'success' };
    if (score >= 80) return { text: 'Good', variant: 'primary' };
    if (score >= 70) return { text: 'Average', variant: 'warning' };
    if (score >= 60) return { text: 'Below Average', variant: 'danger' };
    return { text: 'Poor', variant: 'danger' };
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return { text: 'Active', variant: 'success' };
      case 'inactive':
        return { text: 'Inactive', variant: 'secondary' };
      case 'suspended':
        return { text: 'Suspended', variant: 'danger' };
      default:
        return { text: 'Unknown', variant: 'secondary' };
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle student actions
  const handleViewStudent = (studentId) => {
    navigate(`/students/${studentId}`);
  };

  const handleEditStudent = (studentId) => {
    navigate(`/students/${studentId}/edit`);
  };

  const handleDeleteStudent = async (studentId) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await studentsAPI.deleteStudent(studentId);
        showSuccess('Student deleted successfully');
        fetchStudents();
      } catch (error) {
        console.error('Error deleting student:', error);
        showError('Failed to delete student');
      }
    }
  };

  const handleAssignTeacher = async (studentId) => {
    const teacherId = window.prompt('Enter Teacher ID to assign (leave blank to assign to yourself)');
    try {
      await studentsAPI.assignToTeacher(studentId, teacherId ? teacherId.trim() : null);
      showSuccess('Student assigned successfully');
      fetchStudents();
    } catch (error) {
      console.error('Error assigning student:', error);
      showError('Failed to assign student');
    }
  };

  const handleUnassignTeacher = async (studentId) => {
    try {
      await studentsAPI.unassignTeacher(studentId);
      showSuccess('Student unassigned successfully');
      fetchStudents();
    } catch (error) {
      console.error('Error unassigning student:', error);
      showError('Failed to unassign student');
    }
  };

  // Pagination
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirstStudent, indexOfLastStudent);
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

  // Effects
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    filterStudents();
  }, [filterStudents]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-sm text-slate-500">Loading students...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Students</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage and monitor student accounts</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => fetchStudents()} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 bg-white rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
              <FiRefreshCw size={16} /> Refresh
            </button>
            {user?.role === 'admin' && (
              <button type="button" onClick={() => navigate('/students/new')} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800">
                <FiPlus size={16} /> Add student
              </button>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input type="text" placeholder="Search students..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-200 focus:border-slate-300" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={selectedFilter} onChange={(e) => setSelectedFilter(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
                {filterOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white">
                {sortOptions.map(opt => <option key={opt.value} value={opt.value}>Sort: {opt.label}</option>)}
              </select>
              <button type="button" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50" title="Toggle order"><FiTrendingUp size={16} style={{ transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'none' }} /></button>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                <button type="button" onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}><FiGrid size={18} /></button>
                <button type="button" onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}><FiList size={18} /></button>
              </div>
            </div>
          </div>
        </div>

        {filteredStudents.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-12 text-center">
            <FiUsers size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">{user?.role === 'teacher' ? 'No students assigned' : 'No students found'}</h3>
            <p className="text-sm text-slate-500 mb-4">
              {searchTerm || selectedFilter !== 'all' ? 'Try adjusting search or filters.' : user?.role === 'teacher' ? 'Contact an admin to assign students.' : 'Add a student to get started.'}
            </p>
            {searchTerm || selectedFilter !== 'all' ? (
              <button type="button" onClick={() => { setSearchTerm(''); setSelectedFilter('all'); }} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">Clear filters</button>
            ) : user?.role === 'admin' && (
              <button type="button" onClick={() => navigate('/students/new')} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800"><FiPlus size={16} /> Add student</button>
            )}
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-3">Showing {indexOfFirstStudent + 1}-{Math.min(indexOfLastStudent, filteredStudents.length)} of {filteredStudents.length}</p>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentStudents.map((student) => {
                  const statusBadge = getStatusBadge(student.status);
                  const perfBadge = student.performance?.averageScore != null ? getPerformanceBadge(student.performance.averageScore) : null;
                  return (
                    <div key={student._id} className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600"><FiUsers size={20} /></div>
                          <div className="min-w-0">
                            <h3 className="font-medium text-slate-900 truncate">{student.firstName} {student.lastName}</h3>
                            <p className="text-xs text-slate-500 truncate">{student.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button type="button" onClick={() => navigate(`/students/progress/${student._id}`)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Progress"><FiBarChart2 size={14} /></button>
                          <button type="button" onClick={() => handleViewStudent(student._id)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="View"><FiEye size={14} /></button>
                          {user?.role === 'admin' && <button type="button" onClick={() => handleEditStudent(student._id)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Edit"><FiEdit size={14} /></button>}
                          {user?.role === 'admin' && <button type="button" onClick={() => handleAssignTeacher(student._id)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Assign"><FiUsers size={14} /></button>}
                          {user?.role === 'teacher' && <button type="button" onClick={() => handleUnassignTeacher(student._id)} className="p-2 rounded-lg text-red-600 hover:bg-red-50" title="Unassign"><FiTrash2 size={14} /></button>}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs text-slate-500 mb-2">
                        <span className="inline-flex items-center gap-1"><FiCalendar size={12} />{formatDate(student.createdAt)}</span>
                        {student.phone && <span className="inline-flex items-center gap-1"><FiPhone size={12} />{student.phone}</span>}
                      </div>
                      <div className="flex items-center gap-3 text-xs mb-2">
                        <span><FiBookOpen size={12} className="inline mr-1" />{student.performance?.testsCompleted || 0} tests</span>
                        <span><FiAward size={12} className="inline mr-1" />{student.performance?.averageScore ?? 0}% avg</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${badgeClass(statusBadge.variant)}`}>{statusBadge.text}</span>
                        {perfBadge && <span className={`px-2 py-0.5 rounded text-xs font-medium ${badgeClass(perfBadge.variant)}`}>{perfBadge.text}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50/80 border-b border-slate-200">
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Student</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Joined</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Tests</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Score</th>
                    <th className="px-4 py-3 w-0" />
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentStudents.map((student) => {
                      const statusBadge = getStatusBadge(student.status);
                      const perfBadge = student.performance?.averageScore != null ? getPerformanceBadge(student.performance.averageScore) : null;
                      return (
                        <tr key={student._id} className="hover:bg-slate-50/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600"><FiUsers size={16} /></div>
                              <div><div className="font-medium text-slate-900">{student.firstName} {student.lastName}</div><div className="text-xs text-slate-500">{student.email}</div></div>
                            </div>
                          </td>
                          <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${badgeClass(statusBadge.variant)}`}>{statusBadge.text}</span></td>
                          <td className="px-4 py-3 text-slate-600">{formatDate(student.createdAt)}</td>
                          <td className="px-4 py-3 text-slate-600">{student.performance?.testsCompleted || 0}</td>
                          <td className="px-4 py-3 text-slate-600">{student.performance?.averageScore ?? 0}%</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button type="button" onClick={() => navigate(`/students/progress/${student._id}`)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Progress"><FiBarChart2 size={14} /></button>
                              <button type="button" onClick={() => handleViewStudent(student._id)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="View"><FiEye size={14} /></button>
                              {user?.role === 'admin' && <button type="button" onClick={() => handleEditStudent(student._id)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Edit"><FiEdit size={14} /></button>}
                              <button type="button" onClick={() => handleDeleteStudent(student._id)} className="p-2 rounded-lg text-red-600 hover:bg-red-50" title="Delete"><FiTrash2 size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-4">
                <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1} className="px-3 py-2 border border-slate-200 bg-white rounded-lg text-sm font-medium text-slate-700 disabled:opacity-50">Previous</button>
                <span className="text-sm text-slate-600">Page {currentPage} of {totalPages}</span>
                <button type="button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="px-3 py-2 border border-slate-200 bg-white rounded-lg text-sm font-medium text-slate-700 disabled:opacity-50">Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Students;